import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
    ChevronRight,
    ShoppingCart,
    Truck,
    ShieldCheck,
    Heart,
    Share2,
    AlertCircle,
    CalendarIcon,
    Plus,
    Minus,
    Maximize2
} from "lucide-react";
import useEmblaCarousel from 'embla-carousel-react';
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { StoreHeader, StoreFooter, CartSheet } from "@/components/store";
import { getImageUrl } from "../utils/imageUtils";
import { useCart } from "../context/CartContext";
import RelatedProducts from "../components/common/RelatedProducts";
import { cn } from "@/lib/utils";
import { isPlaceholderVariant, uniqueVariants, variantLabel } from "@/lib/variants";
import { useStore } from "@/hooks/useStore";
import { storePath, getSubdomain } from "../utils/storeUtils";
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// API Config
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function ProductDetails() {
    const { id, subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    const navigate = useNavigate();
    const { addToCart, cartCount, setCartOpen } = useCart();

    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [addons, setAddons] = useState([]);
    const [customizations, setCustomizations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Store State (from hook)
    const { store, theme, storeAppearance, platformInfo, loading: storeLoading } = useStore(subdomain);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");


    // Selection State
    const [quantity, setQuantity] = useState(1);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [customizationValues, setCustomizationValues] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    // Gallery State
    const [selectedImage, setSelectedImage] = useState("");
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, skipSnaps: false });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
    }, [emblaApi, onSelect]);

    const scrollTo = useCallback((index) => {
        if (!emblaApi) return;
        emblaApi.scrollTo(index);
    }, [emblaApi]);

    // Derived State: Images
    const productImages = useMemo(() => {
        if (!product) return [];
        // const main = product.image_url || product.images?.[0];
        // const additional = product.images || [];
        // // Combine and unique
        // const all = [main, ...additional].filter(Boolean);
        // return Array.from(new Set(all));
        // Only show from images array as requested. 
        // Fallback to image_url ONLY if images array is totally empty.
        const all = (product.images && product.images.length > 0)
            ? product.images
            : [product.image_url];

        return Array.from(new Set(all)).filter(Boolean);
    }, [product]);

    // Fetch Logic
    useEffect(() => {
        // If we already have product data and we're just switching variants,
        // we can skip the full loading state for a smoother feel.
        fetchProductDetails(product === null);
    }, [id, subdomain]);

    const fetchProductDetails = async (isNewProduct = true) => {
        if (isNewProduct) setLoading(true);
        try {
            const productRes = await publicAxios.get(`${API}/products/${id}`, {
                params: { store_subdomain: subdomain }
            });
            const productData = productRes.data;

            // Fetch Variants
            const parentId = productData.parent_id || productData.product_id;
            const variantsRes = await publicAxios.get(`${API}/products`, {
                params: {
                    parent_id: parentId,
                    store_subdomain: subdomain
                }
            });
            let allVariants = variantsRes.data || [];

            // If current product is a child, fetch parent to allow fallback of missing data.
            if (productData.parent_id) {
                try {
                    const parentRes = await publicAxios.get(`${API}/products/${productData.parent_id}`, {
                        params: { store_subdomain: subdomain }
                    });
                    const parentData = parentRes.data;
                    allVariants.push(parentData);

                    // Fallback logic for variants missing descriptions or images
                    if (!productData.description || productData.description.trim() === '') {
                        productData.description = parentData.description;
                    }
                    if (!productData.image_url && (!productData.images || productData.images.length === 0)) {
                        productData.image_url = parentData.image_url;
                        productData.images = parentData.images;
                    }

                    // If variant name is missing, identical to parent, or just a SKU, append the
                    // variant_name - unless it is Shopify's "Default Title" placeholder.
                    if (!productData.name || productData.name === parentData.name || productData.name === productData.sku) {
                        productData.name = !isPlaceholderVariant(productData.variant_name)
                            ? `${parentData.name} - ${productData.variant_name}`
                            : parentData.name;
                    }

                } catch (e) { console.error("Parent fetch failed", e); }
            } else {
                // If current product IS the parent, ensure it's in the list so it appears as an option
                allVariants.push(productData);
            }

            // Now that productData is fully patched, set it to state
            setProduct(productData);
            const initialImg = productData.image_url || productData.images?.[0] || "/placeholder2.jpg";
            setSelectedImage(initialImg);

            // Deduplicate by product_id, keeping the first record
            setVariants(uniqueVariants(allVariants));

            // Fetch Customizations (Main Product + Selected Add-ons)
            const customizationsRes = await publicAxios.get(`${API}/customizations`, {
                params: { store_subdomain: subdomain }
            });
            const allCust = Array.isArray(customizationsRes.data) ? customizationsRes.data : [];

            // Collect all relevant customization IDs (main product + all possible add-ons)
            const addonIds = productData.addons || [];
            let allRelevantAddons = [];
            if (addonIds.length > 0) {
                const addonsRes = await publicAxios.get(`${API}/products`, {
                    params: {
                        ids: addonIds,
                        store_subdomain: subdomain
                    }
                });
                allRelevantAddons = addonsRes.data.products || addonsRes.data || [];
                setAddons(allRelevantAddons);
            } else {
                setAddons([]); // Clear addons if none exist for this product/variant
            }

            const mainCustIds = [
                ...(productData.customizations?.mandatory || []),
                ...(productData.customizations?.optional || [])
            ];

            const addonCustIds = allRelevantAddons.reduce((acc, addon) => {
                return [...acc, ...(addon.customizations?.mandatory || []), ...(addon.customizations?.optional || [])];
            }, []);

            const allNeededIds = Array.from(new Set([...mainCustIds, ...addonCustIds]));
            const relevantCust = allCust.filter(c => allNeededIds.includes(c.customization_id));
            setCustomizations(relevantCust);

            // Reset selection state (except maybe addons if we want to persist between variations?)
            setQuantity(1);
            setCustomizationValues({});
            setValidationErrors({});

        } catch (error) {
            console.error("Failed to fetch product details:", error);
            toast.error("Failed to load product details");
        } finally {
            setLoading(false);
        }
    };

    // Helper to get structured customization breakdown
    const getCustomizationBreakdown = useCallback((productObj, values, allCusts, multiplier = 1) => {
        const mandatoryIds = productObj.customizations?.mandatory || [];
        const optionalIds = productObj.customizations?.optional || [];
        const relevantIds = [...mandatoryIds, ...optionalIds];

        let total = 0;
        const breakdown = [];

        allCusts.filter(c => relevantIds.includes(c.customization_id)).forEach(c => {
            let isUsed = mandatoryIds.includes(c.customization_id);
            const fieldsUsed = [];
            let fieldsTotal = 0;
            let rawFieldsTotal = 0;

            if (c.form_fields) {
                c.form_fields.forEach((field, i) => {
                    const key = `${c.customization_id}_${i}`;
                    const val = values[key];
                    if (val) {
                        isUsed = true;
                        let modifier = 0;
                        if (field.field_type === 'dropdown' || field.field_type === 'radio') {
                            const selectedOption = field.options?.find(opt => opt.label === val);
                            if (selectedOption) modifier = Number(selectedOption.price_modifier || 0);
                        } else if (field.field_type === 'checkbox' && val) {
                            modifier = Number(field.options?.[0]?.price_modifier || 0);
                        }

                        // Apply multiplier to field modifier
                        const multipliedModifier = modifier * multiplier;

                        fieldsUsed.push({
                            label: field.label || `Field ${i + 1}`,
                            value: val,
                            price_modifier: multipliedModifier,
                            raw_price_modifier: modifier
                        });
                        fieldsTotal += multipliedModifier;
                        rawFieldsTotal += modifier;
                    }
                });
            }

            if (isUsed) {
                const rawBasePrice = Number(c.price || 0);
                const multipliedBasePrice = rawBasePrice * multiplier;

                breakdown.push({
                    id: c.customization_id,
                    name: c.name,
                    base_price: multipliedBasePrice,
                    raw_base_price: rawBasePrice,
                    fields: fieldsUsed,
                    total: multipliedBasePrice + fieldsTotal,
                    raw_total: rawBasePrice + rawFieldsTotal
                });
                total += (multipliedBasePrice + fieldsTotal);
            }
        });

        return { total, breakdown };
    }, []);

    // Total Price Calculation
    const { totalPrice, mainBreakdown, addonsBreakdown } = useMemo(() => {
        if (!product) return { totalPrice: 0, mainBreakdown: [], addonsBreakdown: {} };
        const base = product.display_price || product.base_price || 0;

        // Calculate multiplier
        const multiplier = (product.base_price && product.display_price) ? (product.display_price / product.base_price) : 1;

        // Main product breakdown
        const { total: mainCustTotal, breakdown: mainCustBreakdown } = getCustomizationBreakdown(product, customizationValues, customizations, multiplier);

        // Add-ons and their breakdowns
        let addonsTotal = 0;
        const addonsBr = {};
        addons.filter(a => selectedAddons.includes(a.product_id)).forEach(addon => {
            const addonMultiplier = (addon.base_price && addon.display_price) ? (addon.display_price / addon.base_price) : multiplier;
            const { total: addonCustTotal, breakdown: addonCustBreakdown } = getCustomizationBreakdown(addon, customizationValues, customizations, addonMultiplier);
            const addonBase = addon.display_price || addon.base_price || 0;
            addonsTotal += (addonBase + addonCustTotal);
            addonsBr[addon.product_id] = {
                base: addonBase,
                total: addonBase + addonCustTotal,
                customizations: addonCustBreakdown
            };
        });

        const total = base + mainCustTotal + addonsTotal;
        return { totalPrice: total * quantity, mainBreakdown: mainCustBreakdown, addonsBreakdown: addonsBr };
    }, [product, customizationValues, customizations, selectedAddons, addons, quantity, getCustomizationBreakdown]);

    // Validation
    const validateForm = () => {
        const errors = {};

        // 1. Validate Main Product Mandatory Customizations
        const mainMandatoryIds = product.customizations?.mandatory || [];
        customizations.forEach(c => {
            const isMandatory = mainMandatoryIds.includes(c.customization_id);
            const isOptional = product.customizations?.optional?.includes(c.customization_id);

            if ((isMandatory || isOptional) && c.form_fields) {
                c.form_fields.forEach((field, i) => {
                    // Logic: Required if (Mandatory in Product) OR (Required in Global AND NOT Optional in Product)
                    const isRequired = isMandatory || (field.required && !isOptional);

                    if (isRequired) {
                        const key = `${c.customization_id}_${i}`;
                        if (!customizationValues[key]) {
                            errors[key] = "This field is required";
                        }
                    }
                });
            }
        });

        // 2. Validate Selected Add-ons Mandatory Customizations
        selectedAddons.forEach(addonId => {
            const addon = addons.find(a => a.product_id === addonId);
            if (addon) {
                const addonMandatory = addon.customizations?.mandatory || [];
                const addonOptional = addon.customizations?.optional || [];

                // Combine both lists to find relevant customizations to check
                const relevantIds = [...addonMandatory, ...addonOptional];

                relevantIds.forEach(custId => {
                    const cust = customizations.find(c => c.customization_id === custId);
                    if (cust && cust.form_fields) {
                        const isMandatory = addonMandatory.includes(custId);
                        const isOptional = addonOptional.includes(custId);

                        cust.form_fields.forEach((field, i) => {
                            const isRequired = isMandatory || (field.required && !isOptional);

                            if (isRequired) {
                                const key = `${custId}_${i}`;
                                if (!customizationValues[key]) {
                                    errors[key] = "This field is required for the selected add-on";
                                }
                            }
                        });
                    }
                });
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddToCart = () => {
        if (!validateForm()) {
            toast.error("Please fill in all required fields.");
            return;
        }

        // 1. Add Main Product
        const mainItemCustBaseTotal = mainBreakdown.reduce((s, c) => {
            // We need to get the UNMULTIPLIED base price. 
            // Since we applied multiplier in getCustomizationBreakdown, 
            // we should have kept the raw value. 
            // I'll update getCustomizationBreakdown to return raw_total too.
            return s + (c.raw_total || 0);
        }, 0);

        const mainItem = {
            uniqueKey: `${product.product_id}-${Date.now()}-main`,
            product_id: product.product_id,
            product_type: (product.store_id || product.source === 'custom') ? 'custom' : 'main',
            name: product.name,
            base_price: (product.base_price || 0) + mainItemCustBaseTotal,
            total_price: (product.display_price || product.base_price || 0) + mainBreakdown.reduce((s, c) => s + c.total, 0),
            image_url: product.image_url || product.images?.[0],
            quantity,
            selectedAddons: [],
            customizations: mainBreakdown, // Now sending the structured array
            variant_name: isPlaceholderVariant(product.variant_name) ? undefined : product.variant_name,
            store_subdomain: subdomain,
            // Product Management v3 — carry the sales channel for commission/payout.
            channel: product.channel || 'edisplay'
        };
        addToCart(mainItem);

        // 2. Add Selected Addons
        selectedAddons.forEach((addonId, index) => {
            const addon = addons.find(a => a.product_id === addonId);
            const breakdown = addonsBreakdown[addonId];
            if (addon && breakdown) {
                const addonCustBaseTotal = breakdown.customizations.reduce((s, c) => s + (c.raw_total || 0), 0);

                const addonItem = {
                    uniqueKey: `${addon.product_id}-${Date.now()}-addon-${index}`,
                    product_id: addon.product_id,
                    product_type: (addon.store_id || addon.source === 'custom') ? 'custom' : 'main',
                    name: addon.name,
                    base_price: (addon.base_price || 0) + addonCustBaseTotal,
                    total_price: addon.display_price + breakdown.total, // breakdown.total is just customization total
                    image_url: addon.image_url || addon.images?.[0],
                    quantity, // Usually same as main product
                    customizations: breakdown.customizations,
                    store_subdomain: subdomain,
                    channel: product.channel || 'edisplay' // bill add-ons on the parent's channel
                };
                addToCart(addonItem);
            }
        });

        toast.success("Added to cart!");
    };

    // Rendering Helpers
    const renderCustomField = (cust, field, index) => {
        const productMandatory = product.customizations?.mandatory || [];
        const productOptional = product.customizations?.optional || [];

        // Check if mandatory in main product
        let isMandatory = productMandatory.includes(cust.customization_id);
        let isOptional = productOptional.includes(cust.customization_id);

        // Check if mandatory in any selected addon
        const addonWithCust = addons.find(a =>
            selectedAddons.includes(a.product_id) &&
            (a.customizations?.mandatory?.includes(cust.customization_id) || a.customizations?.optional?.includes(cust.customization_id))
        );

        if (addonWithCust) {
            if (addonWithCust.customizations?.mandatory?.includes(cust.customization_id)) isMandatory = true;
            if (addonWithCust.customizations?.optional?.includes(cust.customization_id)) isOptional = true;
        }

        const isRequired = field.required ? !isOptional : isMandatory;
        const key = `${cust.customization_id}_${index}`;

        const handleChange = (val) => {
            setCustomizationValues(prev => ({ ...prev, [key]: val }));
            if (validationErrors[key]) {
                setValidationErrors(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
        };

        const error = validationErrors[key];
        const val = customizationValues[key] || "";

        switch (field.field_type) {
            case 'text':
                return (
                    <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                            {field.label || cust.name} {isRequired && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                            placeholder={field.placeholder || "Enter details..."}
                            value={val}
                            onChange={(e) => handleChange(e.target.value)}
                            maxLength={field.character_limit}
                            className={cn(error && "border-red-500 focus:ring-red-500")}
                        />
                        {field.character_limit && (
                            <p className="text-[10px] text-slate-400 text-right">
                                {val.length} / {field.character_limit}
                            </p>
                        )}
                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
                    </div>
                );
            case 'textarea':
                return (
                    <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                            {field.label || cust.name} {isRequired && <span className="text-red-500">*</span>}
                        </Label>
                        <Textarea
                            placeholder={field.placeholder || "Enter details..."}
                            value={val}
                            onChange={(e) => handleChange(e.target.value)}
                            maxLength={field.character_limit}
                            className={cn(error && "border-red-500 focus:ring-red-500")}
                        />
                        {field.character_limit && (
                            <p className="text-[10px] text-slate-400 text-right">
                                {val.length} / {field.character_limit}
                            </p>
                        )}
                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
                    </div>
                );
            case 'dropdown':
                return (
                    <div className="space-y-1.5">
                        <Label className="flex items-center gap-1">
                            {field.label || cust.name} {isRequired && <span className="text-red-500">*</span>}
                        </Label>
                        <Select
                            onValueChange={handleChange}
                            value={val}
                        >
                            <SelectTrigger className={cn(error && "border-red-500")}>
                                <SelectValue placeholder={field.placeholder || `Select ...`} />
                            </SelectTrigger>
                            <SelectContent>
                                {field.options?.map((opt, i) => (
                                    <SelectItem key={i} value={opt.label}>
                                        {opt.label} {opt.price_modifier > 0 && `(+$${opt.price_modifier})`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
                    </div>
                );
            case 'radio':
                return (
                    <div className="space-y-1.5">
                        <Label className="flex items-center gap-1">
                            {field.label || cust.name} {isRequired && <span className="text-red-500">*</span>}
                        </Label>
                        <RadioGroup
                            onValueChange={handleChange}
                            value={val}
                            className="flex flex-wrap gap-3"
                        >
                            {field.options?.map((opt, i) => (
                                <div key={i} className="flex items-center space-x-2">
                                    <RadioGroupItem value={opt.label} id={`${key}-${i}`} />
                                    <Label htmlFor={`${key}-${i}`} className="text-sm font-normal">
                                        {opt.label} {opt.price_modifier > 0 && <span className="text-amber-700 font-bold ml-1">+${opt.price_modifier}</span>}
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>
                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
                    </div>
                );
            case 'date':
                return (
                    <div className="space-y-1.5">
                        <Label className="flex items-center gap-1">
                            {field.label || cust.name} {isRequired && <span className="text-red-500">*</span>}
                        </Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-full justify-start text-left font-normal h-11",
                                        !val && "text-muted-foreground",
                                        error && "border-red-500"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {val ? (
                                        format(new Date(val), "PPP")
                                    ) : (
                                        <span>Pick a date</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={val ? new Date(val) : undefined}
                                    onSelect={(date) => handleChange(date?.toISOString())}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="flex items-center space-x-2 py-2">
                        <Checkbox
                            id={key}
                            checked={!!val}
                            onCheckedChange={(checked) => handleChange(checked)}
                        />
                        <Label htmlFor={key} className="text-sm cursor-pointer leading-none">
                            {field.label || cust.name} {field.options?.[0]?.price_modifier > 0 && `(+$${field.options[0].price_modifier})`}
                        </Label>
                    </div>
                );
            default:
                return null;
        }
    };

    const activeCollections = useMemo(() => {
        return (product?.collections || []).filter(c => c.is_active !== false);
    }, [product]);

    const currentCollection = activeCollections[0];

    return (
        <div className="bg-slate-50/30 animate-fade-in">
            {!subdomain && <Header />}

            {/* Breadcrumbs */}
            <div className="bg-white border-b py-3 px-4 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {loading || storeLoading ? (
                        <div className="h-4 w-48 rounded animate-shimmer" />
                    ) : (
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink asChild>
                                        <Link to={subdomain ? storePath(subdomain, "/") : "/"}>Home</Link>
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                {currentCollection ? (
                                    <>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild>
                                                <Link to={subdomain ? storePath(subdomain, `/shop?collection_id=${currentCollection.collection_id}`) : `/shop?collection_id=${currentCollection.collection_id}`}>
                                                    {currentCollection.name}
                                                </Link>
                                            </BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                    </>
                                ) : (
                                    <>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink asChild>
                                                <Link to={subdomain ? storePath(subdomain, `/products`) : "/products"}>Products</Link>
                                            </BreadcrumbLink>
                                        </BreadcrumbItem>
                                        <BreadcrumbSeparator />
                                    </>
                                )}
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="font-medium text-slate-900 truncate max-w-[200px]">
                                        {product?.name}
                                    </BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 md:py-12 min-h-[60vh]">
                {loading || storeLoading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-in fade-in duration-500">
                        {/* Gallery Skeleton */}
                        <div className="lg:col-span-7">
                            <div className="aspect-square rounded-3xl animate-shimmer" />
                            <div className="flex gap-3 mt-6 justify-center">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="w-20 h-20 rounded-2xl animate-shimmer" />
                                ))}
                            </div>
                        </div>
                        {/* Details Skeleton */}
                        <div className="lg:col-span-5 space-y-6">
                            <div className="w-1/4 h-4 rounded animate-shimmer" />
                            <div className="w-3/4 h-12 rounded animate-shimmer" />
                            <div className="w-full h-32 rounded animate-shimmer" />
                            <div className="w-1/2 h-8 rounded animate-shimmer" />
                            <div className="w-full h-48 rounded animate-shimmer" />
                        </div>
                    </div>
                ) : !product ? (
                    <div className="flex flex-col items-center justify-center h-96 gap-4">
                        <h2 className="text-2xl font-bold text-slate-900">Product not found</h2>
                        <Link to="/products"><Button>Return to Shop</Button></Link>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-in fade-in slide-in-from-bottom-8 duration-700">

                            {/* LEFT COLUMN: Gallery */}
                            <div className="lg:col-span-7 space-y-6">
                                <div className="sticky top-28">
                                    <div className="relative group">
                                        <div className="overflow-hidden rounded-3xl bg-white aspect-square border-4 border-white shadow-xl" ref={emblaRef}>
                                            <div className="flex h-full">
                                                {productImages.map((img, i) => (
                                                    <div key={i} className="flex-[0_0_100%] min-w-0 relative h-full flex items-center justify-center p-8 cursor-zoom-in">
                                                        <img
                                                            src={getImageUrl(img)}
                                                            alt={`${product.name} - View ${i + 1}`}
                                                            className="max-h-full max-w-full object-contain mix-blend-multiply transition-transform duration-500 hover:scale-110"
                                                            loading={i === 0 ? "eager" : "lazy"}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="absolute top-4 right-4 bg-white/80 backdrop-blur p-2 rounded-full shadow-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Maximize2 className="w-5 h-5 text-slate-600" />
                                        </div>
                                    </div>

                                    {/* Thumbnails */}
                                    {productImages.length > 1 && (
                                        <div className="flex gap-3 mt-6 pb-2 scrollbar-hide justify-center">
                                            {productImages.map((img, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => scrollTo(i)}
                                                    className={cn(
                                                        "w-20 h-20 rounded-2xl bg-white border-2 flex-shrink-0 p-2 transition-all overflow-hidden",
                                                        selectedIndex === i ? "border-amber-600 shadow-md scale-105" : "border-slate-100 hover:border-slate-300"
                                                    )}
                                                >
                                                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT COLUMN: Details */}
                            <div className="lg:col-span-5 space-y-8">
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                                        {activeCollections.length > 0 ? (
                                            activeCollections.map((c, i) => (
                                                <Link
                                                    key={c.collection_id || i}
                                                    to={subdomain ? storePath(subdomain, `/shop?collection_id=${c.collection_id}`) : `/shop?collection_id=${c.collection_id}`}
                                                    className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all hover:scale-105 active:scale-95 shadow-sm border"
                                                    style={{ 
                                                        backgroundColor: `${theme.accent_color}15`, 
                                                        color: theme.accent_color,
                                                        borderColor: `${theme.accent_color}30`
                                                    }}
                                                >
                                                    {c.name}
                                                </Link>
                                            ))
                                        ) : (
                                            <p className="text-amber-700 font-bold uppercase tracking-[0.2em] text-xs">
                                                All Products
                                            </p>
                                        )}
                                    </div>
                                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
                                        {product.name}
                                    </h1>
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                        <div className="space-y-1">
                                            <p className="text-xs text-slate-400 font-mono tracking-tighter uppercase">SKU: {product.sku || 'N/A'}</p>
                                            <div className="flex items-center gap-2">
                                                <span className={cn("w-2 h-2 rounded-full", product.is_active !== false ? "bg-green-500" : "bg-red-500")} />
                                                <span className="text-xs font-bold text-slate-600">
                                                    {product.is_active !== false ? 'In Stock' : 'Not Available'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-4">
                                                <span className="text-3xl font-bold" style={{ color: theme.accent_color }}>
                                                    ${totalPrice.toFixed(2)}
                                                </span>
                                                {totalPrice > (product.display_price || product.base_price) && (
                                                    <span className="text-sm text-slate-500 line-through">
                                                        ${(product.display_price || product.base_price).toFixed(2)} Base
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Pricing in USD</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="prose prose-slate prose-sm max-w-none text-slate-600">
                                    <p className="leading-relaxed whitespace-pre-line">{product.description}</p>
                                </div>

                                {/* Variants Selector */}
                                {variants.length > 1 && (
                                    <div className="space-y-3">
                                        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Available Variations</Label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {variants.map(v => (
                                                <Button
                                                    key={v.product_id}
                                                    variant="outline"
                                                    onClick={() => {
                                                        // 1. Instant state update for better feedback
                                                        // setProduct(v); // Don't set product immediately, let fetching handle it to prevent flicker
                                                        // setSelectedImage(v.image_url || v.images?.[0] || "/placeholder2.jpg");

                                                        // 2. Context-aware navigation
                                                        const path = subdomain
                                                            ? storePath(subdomain, `/product/${v.product_id}`)
                                                            : `/product/${v.product_id}`;
                                                        navigate(path, { replace: true });
                                                    }}
                                                    className={cn(
                                                        "h-auto min-h-[56px] py-2 px-3 flex flex-col items-center justify-center gap-1 rounded-xl transition-all",
                                                        v.product_id === (product?.product_id || id)
                                                            ? "border-amber-600 bg-amber-50/50 text-amber-900 shadow-sm"
                                                            : "border-slate-200 hover:border-amber-400"
                                                    )}
                                                >
                                                    <span className="text-xs font-bold whitespace-normal break-words text-center leading-tight">
                                                        {variantLabel(v, (variants.find(x => !x.parent_id) || product).name)}
                                                    </span>
                                                    <span className="text-[10px] opacity-60 mt-0.5">${(v.display_price || v.base_price)?.toFixed(2)}</span>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Add-ons */}
                                {addons.length > 0 && (
                                    <div className="space-y-4 p-5 bg-white rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Enhance your purchase</Label>
                                            <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">Add-ons</span>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2">
                                            {addons.map(addon => (
                                                <div
                                                    key={addon.product_id}
                                                    onClick={() => {
                                                        setSelectedAddons(prev =>
                                                            prev.includes(addon.product_id)
                                                                ? prev.filter(id => id !== addon.product_id)
                                                                : [...prev, addon.product_id]
                                                        );
                                                    }}
                                                    className={cn(
                                                        "flex items-center gap-4 p-3 rounded-2xl cursor-pointer transition-all border-2",
                                                        selectedAddons.includes(addon.product_id)
                                                            ? "bg-slate-900 border-slate-900 text-white"
                                                            : "bg-slate-50 border-transparent hover:bg-slate-100"
                                                    )}
                                                >
                                                    <div className="w-12 h-12 rounded-xl bg-white overflow-hidden p-1 flex-shrink-0">
                                                        <img src={getImageUrl(addon.image_url || addon.images?.[0])} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-bold truncate">{addon.name}</p>
                                                        <p className={cn("text-xs font-medium", selectedAddons.includes(addon.product_id) ? "text-amber-400" : "text-amber-700")}>
                                                            +${(addon.display_price || addon.base_price)?.toFixed(2)}
                                                        </p>
                                                    </div>
                                                    <Checkbox checked={selectedAddons.includes(addon.product_id)} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Customizations Form (Main Product) */}
                                {customizations.filter(c =>
                                    product.customizations?.mandatory?.includes(c.customization_id) ||
                                    product.customizations?.optional?.includes(c.customization_id)
                                ).length > 0 && (
                                        <div className="space-y-6 pt-6 border-t border-slate-100">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Personalization</Label>
                                            </div>
                                            <div className="space-y-5">
                                                {customizations.filter(c =>
                                                    product.customizations?.mandatory?.includes(c.customization_id) ||
                                                    product.customizations?.optional?.includes(c.customization_id)
                                                ).map(cust => (
                                                    <div key={cust.customization_id} className="space-y-4">
                                                        <h5 className="text-[11px] font-bold text-slate-900 border-b border-slate-100 pb-1 uppercase tracking-wider">
                                                            {cust.name}
                                                        </h5>
                                                        <div className="space-y-4 pl-1">
                                                            {cust.form_fields?.map((field, i) => (
                                                                <div key={`${cust.customization_id}-${i}`}>
                                                                    {renderCustomField(cust, field, i)}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                {/* Add-on Customizations (Dynamic) */}
                                <div className="space-y-6 mt-6">
                                    {addons
                                        .filter(a => selectedAddons.includes(a.product_id))
                                        .map(addon => {
                                            const mandatoryCusts = customizations.filter(c => addon.customizations?.mandatory?.includes(c.customization_id));
                                            const optionalCusts = customizations.filter(c => addon.customizations?.optional?.includes(c.customization_id));

                                            return (
                                                <div key={addon.product_id} className="space-y-4">
                                                    {/* Mandatory Card */}
                                                    {mandatoryCusts.length > 0 && (
                                                        <div className="p-6 bg-amber-50/30 rounded-3xl border border-amber-100 shadow-sm space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                                                            <div className="flex items-center gap-2 text-amber-800">
                                                                <AlertCircle size={16} />
                                                                <span className="text-xs font-bold uppercase tracking-widest">Mandatory Customization for {addon.name}</span>
                                                            </div>
                                                            <div className="space-y-6">
                                                                {mandatoryCusts.map(cust => (
                                                                    <div key={cust.customization_id} className="space-y-3">
                                                                        <h5 className="text-[11px] font-bold text-amber-900/70 border-b border-amber-100/50 pb-1 uppercase tracking-wider">
                                                                            {cust.name}
                                                                        </h5>
                                                                        <div className="space-y-4 pl-1">
                                                                            {cust.form_fields?.map((field, i) => (
                                                                                <div key={`${cust.customization_id}-${i}`}>
                                                                                    {renderCustomField(cust, field, i)}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Optional Card */}
                                                    {optionalCusts.length > 0 && (
                                                        <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 shadow-sm space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                                                            <div className="flex items-center gap-2 text-slate-600">
                                                                <span className="text-xs font-bold uppercase tracking-widest">Optional Customization for {addon.name}</span>
                                                            </div>
                                                            <div className="space-y-6">
                                                                {optionalCusts.map(cust => (
                                                                    <div key={cust.customization_id} className="space-y-3">
                                                                        <h5 className="text-[11px] font-bold text-slate-500 border-b border-slate-200 pb-1 uppercase tracking-wider">
                                                                            {cust.name}
                                                                        </h5>
                                                                        <div className="space-y-4 pl-1">
                                                                            {cust.form_fields?.map((field, i) => (
                                                                                <div key={`${cust.customization_id}-${i}`}>
                                                                                    {renderCustomField(cust, field, i)}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>


                                {/* Price Breakdown */}
                                {(mainBreakdown.length > 0 || Object.keys(addonsBreakdown).length > 0) && (
                                    <div className="card-elevated p-6 rounded-3xl border-indigo-50 bg-white/50 backdrop-blur-sm space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 mt-8">
                                        <div className="flex items-center gap-2 text-indigo-600">
                                            <Maximize2 size={16} />
                                            <span className="text-xs font-bold uppercase tracking-widest">Price Breakdown</span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Base Product</span>
                                                <span className="font-bold text-slate-900">${(product.display_price || product.base_price || 0).toFixed(2)}</span>
                                            </div>

                                            {mainBreakdown.map(cust => (
                                                <div key={cust.id} className="space-y-1">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-700 font-medium">{cust.name}</span>
                                                        <span className="font-bold text-slate-900">${cust.total.toFixed(2)}</span>
                                                    </div>
                                                    {cust.fields.map((f, i) => (
                                                        <div key={i} className="flex justify-between text-[10px] pl-4 italic text-slate-400">
                                                            <span>{f.label}: {f.value}</span>
                                                            {f.price_modifier > 0 && <span>+${f.price_modifier.toFixed(2)}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}

                                            {Object.keys(addonsBreakdown).map((addonId) => (
                                                <div key={addonId} className="pt-2 border-t border-slate-100">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-indigo-600 font-bold">Add-on: {addons.find(a => a.product_id === addonId)?.name}</span>
                                                        <span className="font-bold text-indigo-600">${addonsBreakdown[addonId].total.toFixed(2)}</span>
                                                    </div>
                                                    {addonsBreakdown[addonId].customizations.map(cust => (
                                                        <div key={cust.id} className="space-y-1 mt-1">
                                                            <div className="flex justify-between text-[11px] pl-2 text-slate-600 font-medium">
                                                                <span>{cust.name}</span>
                                                                <span>+${cust.total.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Order Controls */}
                                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-xl border-t border-slate-100 z-40 lg:relative lg:p-0 lg:bg-transparent lg:border-none lg:mt-10">
                                    <div className="max-w-7xl mx-auto flex flex-col gap-4">
                                        <div className="flex items-center justify-between lg:hidden mb-2">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Price</span>
                                                <span className="text-2xl font-bold text-slate-900">${totalPrice.toFixed(2)}</span>
                                            </div>
                                            <div className="flex items-center bg-slate-100 p-1 rounded-full border border-slate-200">
                                                <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full hover:bg-white" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                                                    <Minus size={16} />
                                                </Button>
                                                <span className="w-10 text-center font-bold">{quantity}</span>
                                                <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full hover:bg-white" onClick={() => setQuantity(quantity + 1)}>
                                                    <Plus size={16} />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="flex gap-4">
                                            <div className="hidden lg:flex items-center bg-white p-1 rounded-full border border-slate-200 shadow-sm">
                                                <Button variant="ghost" size="icon" className="h-12 w-12 rounded-full hover:bg-slate-50" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                                                    <Minus size={18} />
                                                </Button>
                                                <span className="w-12 text-center font-bold text-lg">{quantity}</span>
                                                <Button variant="ghost" size="icon" className="h-12 w-12 rounded-full hover:bg-slate-50" onClick={() => setQuantity(quantity + 1)}>
                                                    <Plus size={18} />
                                                </Button>
                                            </div>

                                            <Button
                                                size="lg"
                                                onClick={handleAddToCart}
                                                disabled={product.is_active === false}
                                                className="flex-1 h-14 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-lg shadow-2xl shadow-slate-900/20 group uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ShoppingCart className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                                                {product.is_active === false ? "Not Available" : "Add to Cart"} <span className="mx-2 opacity-30">|</span> <span className="hidden sm:inline">${totalPrice.toFixed(2)}</span>
                                            </Button>

                                            <Button variant="outline" size="lg" className="h-14 w-14 rounded-full p-0 border-slate-200 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all flex-shrink-0">
                                                <Heart className="w-6 h-6" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Trust Badges - Hidden as per request */}
                                {/* 
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-white rounded-3xl shadow-sm border border-slate-50 mt-12 overflow-hidden relative">
                                    <div className="flex flex-col items-center text-center gap-2 group">
                                        <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
                                            <Truck size={20} />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase text-slate-400 leading-tight">Fast<br />Shipping</span>
                                    </div>
                                    <div className="flex flex-col items-center text-center gap-2 group">
                                        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600 group-hover:scale-110 transition-transform">
                                            <ShieldCheck size={20} />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase text-slate-400 leading-tight">Secure<br />Checkout</span>
                                    </div>
                                    <div className="flex flex-col items-center text-center gap-2 group">
                                        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
                                            <Heart size={20} />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase text-slate-400 leading-tight">Satisfaction<br />Guaranteed</span>
                                    </div>
                                    <div className="flex flex-col items-center text-center gap-2 group cursor-pointer">
                                        <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600 group-hover:scale-110 transition-transform">
                                            <Share2 size={20} />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase text-slate-400 leading-tight">Share<br />Product</span>
                                    </div>
                                </div>
                                */}
                            </div>
                        </div>

                        {/* Similar Items / Related Products */}
                        {product && (
                            <div className="mt-20 border-t border-slate-100 pt-16">
                                <div className="text-center mb-12">
                                    <h2 className="text-3xl font-bold mb-4" style={{ color: theme.primary_color }}>You May Also Like</h2>
                                    <div className="w-20 h-1 bg-amber-600 mx-auto rounded-full opacity-20"></div>
                                </div>

                                {/* We need a list of related products. Ideally fetching them. 
                               For now, let's try to show some items if available, or just a placeholder 
                               that implies we need to fetch them. 
                               Actually, let's fetch related products in the useEffect or use a separate component.
                               To keep it simple and actionable, I will add a placeholder grid 
                               that would be populated by `relatedProducts`.
                           */}
                                {/* Implementing a simple fetch for related products would be better. */}
                                <RelatedProducts
                                    currentProductId={product.product_id}
                                    categoryId={product.category_id}
                                    subdomain={subdomain}
                                    theme={theme}
                                />
                            </div>
                        )}
                    </>
                )}
            </main>

            {!subdomain && <Footer />}
        </div >
    );
}
